import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { GamificationAchievement, GamificationAchievementFilter, GamificationLeader, GamificationLeaderFilter, PartnershipOffer, PartnershipTransaction, PurchaseOrder, SchoolContentItem, SchoolCourse, StudentLibrary } from "@axl-dev/school-sdk";
import { SchoolEZEditorView } from "@app/pages/learn/components/SchoolEZEditorView";
import { SchoolContentSectionType } from "@app/pages/school-materials/model/school-content-item";
import { useCabinet } from "@app/shared/lib/cabinet/CabinetProvider";
import { CabinetComponent } from "@app/shared/lib/cabinet/types";
import { useLoc, useNotifier, useRouter, useSdk } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { LoadingOverlay } from "@app/shared/ui";
import { CourseListWidget, parseCourseListProps } from "@app/widgets/CourseListWidget";
import type { CourseListWidgetProps } from "@app/widgets/CourseListWidget";
import { CertificateListWidget, parseCertificateListProps } from "@app/widgets/CertificateListWidget";
import type { CertificateListWidgetProps } from "@app/widgets/CertificateListWidget";
import { PaymentMethodListWidget, parsePaymentMethodListProps } from "@app/widgets/PaymentMethodListWidget";
import type { PaymentMethodListWidgetProps } from "@app/widgets/PaymentMethodListWidget";
import { AccessListWidget, parseAccessListProps } from "@app/widgets/AccessListWidget";
import type { AccessListWidgetProps } from "@app/widgets/AccessListWidget";
import { GamificationWidget, parseGamificationProps } from "@app/widgets/GamificationWidget";
import type { GamificationWidgetProps } from "@app/widgets/GamificationWidget";
import { LeaderboardWidget, parseLeaderboardProps } from "@app/widgets/LeaderboardWidget";
import type { LeaderboardWidgetProps } from "@app/widgets/LeaderboardWidget";
import { PartnershipWidget, parsePartnershipProps } from "@app/widgets/PartnershipWidget";
import type { PartnershipWidgetProps } from "@app/widgets/PartnershipWidget";
import { ForumWidget, parseForumProps } from "@app/widgets/ForumWidget";
import type { ForumWidgetProps } from "@app/widgets/ForumWidget";
import { GroupChatWidget, parseGroupChatProps } from "@app/widgets/GroupChatWidget";
import type { GroupChatWidgetProps } from "@app/widgets/GroupChatWidget";
import { initSlider, parseSliderProps } from "@app/widgets/SliderWidget";
import { initAccordion, parseAccordionProps } from "@app/widgets/AccordionWidget";
import { initVendorAppBlock, parseVendorAppBlockProps } from "@app/widgets/VendorAppBlockWidget";

type Props = { segment?: string };

const noScrollStyle = { height: "auto", overflow: "visible" };
const stickyHeaderStyle = { ...noScrollStyle, position: "sticky" as const, top: 0, zIndex: "var(--z-sticky, 100)" };
const stickyFooterStyle = { ...noScrollStyle, position: "sticky" as const, bottom: 0, zIndex: "var(--z-sticky, 100)" };
const pageContainerStyle: React.CSSProperties = { display: "flex", flexDirection: "column", minHeight: "100%" };
const contentStyle: React.CSSProperties = { flex: 1, height: "auto", overflow: "visible" };
const footerPushStyle: React.CSSProperties = { marginTop: "auto", height: "auto", overflow: "visible" };

const ComponentView: React.FC<{
  component: CabinetComponent;
  type: "header" | "footer";
  userIsInFilter: (contactId: string, filterId: string) => Promise<boolean>;
  currentUserId: () => string | undefined;
  clickActions?: Record<string, any>;
  formProps?: any;
}> = ({ component, type, userIsInFilter, currentUserId, clickActions, formProps }) => {
  const baseStyle = type === "footer" ? footerPushStyle : noScrollStyle;
  const wrapperStyle = component.isFixed
    ? type === "header" ? stickyHeaderStyle : { ...stickyFooterStyle, marginTop: "auto" }
    : baseStyle;
  return (
    <SchoolEZEditorView
      key={component.id}
      html={component.designHTMLPublished ?? ""}
      style={component.designStylePublished ?? ""}
      currentUserId={currentUserId}
      userIsInFilter={userIsInFilter}
      wrapperStyle={wrapperStyle}
      clickActions={clickActions}
      formProps={formProps}
    />
  );
};

type CourseListPortal = {
  container: HTMLElement;
  props: CourseListWidgetProps;
};

type CertificateListPortal = {
  container: HTMLElement;
  props: CertificateListWidgetProps;
};

type PaymentMethodListPortal = {
  container: HTMLElement;
  props: PaymentMethodListWidgetProps;
};

type AccessListPortal = {
  container: HTMLElement;
  props: AccessListWidgetProps;
};

type GamificationPortal = {
  container: HTMLElement;
  props: GamificationWidgetProps;
};

type LeaderboardPortal = {
  container: HTMLElement;
  props: LeaderboardWidgetProps;
};

type PartnershipPortal = {
  container: HTMLElement;
  props: PartnershipWidgetProps;
};

type GroupChatPortal = {
  container: HTMLElement;
  props: GroupChatWidgetProps;
};

type ForumPortal = {
  container: HTMLElement;
  props: ForumWidgetProps;
};

const extractCourses = (items: SchoolContentItem[]): SchoolCourse[] => {
  const courses: SchoolCourse[] = [];
  for (const item of items) {
    if (item.course) courses.push(item.course);
    if (item.children?.length) courses.push(...extractCourses(item.children));
  }
  return courses;
};

const extractLibraries = (items: SchoolContentItem[]): StudentLibrary[] => {
  const libraries: StudentLibrary[] = [];
  for (const item of items) {
    if (item.library) libraries.push(item.library);
    if (item.children?.length) libraries.push(...extractLibraries(item.children));
  }
  return libraries;
};

export const CabinetPageViewer: React.FC<Props> = ({ segment }) => {
  const { getPageBySegment, rootPages, isLoading, themeCss } = useCabinet();
  const { user } = useUser();
  const sdk = useSdk();
  const loc = useLoc();
  const containerRef = useRef<HTMLDivElement>(null);
  const [courseListPortals, setCourseListPortals] = useState<CourseListPortal[]>([]);
  const [certListPortals, setCertListPortals] = useState<CertificateListPortal[]>([]);
  const [paymentMethodListPortals, setPaymentMethodListPortals] = useState<PaymentMethodListPortal[]>([]);
  const [accessListPortals, setAccessListPortals] = useState<AccessListPortal[]>([]);
  const [gamificationPortals, setGamificationPortals] = useState<GamificationPortal[]>([]);
  const [leaderboardPortals, setLeaderboardPortals] = useState<LeaderboardPortal[]>([]);
  const [prefetchedLeaderboardLeaders, setPrefetchedLeaderboardLeaders] = useState<GamificationLeader[] | undefined>();
  const [partnershipPortals, setPartnershipPortals] = useState<PartnershipPortal[]>([]);
  const [forumPortals, setForumPortals] = useState<ForumPortal[]>([]);
  const [groupChatPortals, setGroupChatPortals] = useState<GroupChatPortal[]>([]);
  const [prefetchedForumData, setPrefetchedForumData] = useState<any>(undefined);
  const [prefetchedCourses, setPrefetchedCourses] = useState<SchoolCourse[] | undefined>();
  const [prefetchedLibraries, setPrefetchedLibraries] = useState<StudentLibrary[] | undefined>();
  const [prefetchedCourseItems, setPrefetchedCourseItems] = useState<SchoolContentItem[] | undefined>();
  const [prefetchedLibraryItems, setPrefetchedLibraryItems] = useState<SchoolContentItem[] | undefined>();
  const [prefetchedCerts, setPrefetchedCerts] = useState<any[] | undefined>();
  const [prefetchedLicenses, setPrefetchedLicenses] = useState<any[] | undefined>();
  const [prefetchedSubscriptions, setPrefetchedSubscriptions] = useState<any[] | undefined>();
  const [prefetchedOrders, setPrefetchedOrders] = useState<any[] | undefined>();
  const [prefetchedGamTotals, setPrefetchedGamTotals] = useState<any[] | undefined>();
  const [prefetchedGamAchievements, setPrefetchedGamAchievements] = useState<GamificationAchievement[] | undefined>();
  const [prefetchedPartTotals, setPrefetchedPartTotals] = useState<any[] | undefined>();
  const [prefetchedPartOffers, setPrefetchedPartOffers] = useState<PartnershipOffer[] | undefined>();
  const [prefetchedPartOrders, setPrefetchedPartOrders] = useState<PurchaseOrder[] | undefined>();
  const [prefetchedPartTransactions, setPrefetchedPartTransactions] = useState<PartnershipTransaction[] | undefined>();
  const [prefetchedPartTransTotals, setPrefetchedPartTransTotals] = useState<any[] | undefined>();

  const currentUserId = useCallback(() => user?.id, [user?.id]);

  const userIsInFilter = useCallback(
    async (contactId: string, filterId: string) => {
      const result = await sdk.api.education.service.contactInFilter(filterId, contactId);
      return !!result.body?.isInFilter;
    },
    [sdk.api.education.service],
  );

  const formProps = useMemo(
    () => ({
      onSubmit: async (data: any) => {
        const result = await sdk.api.education.service.trackForm({
          name: data.name,
          fields: data.fields,
        });
        return { status: result.raw.status, data: result.body };
      },
    }),
    [sdk.api.education.service],
  );

  const page = segment ? getPageBySegment(segment) : rootPages[0];

  const { history } = useRouter();
  const [notify] = useNotifier();

  const clickActions = useMemo(() => ({
    runCustomEvent: async (action: any) => {
      await sdk.api.post("/service/run-custom-event", {
        cabinetPageId: page?.id,
        eventName: action.data,
      });
    },
    fileDownload: async (id: string) => {
      const file = await sdk.api.file.download(id);
      if (file.success && file.body) return file.body;
      return "";
    },
    openContent: async (action: any) => {
      const meta = (action.data2 || {}) as { type?: string; courseId?: string; libraryId?: string };
      const contentType = meta.type || "course";
      const targetId = action.data as string | undefined;
      if (!targetId) return;

      if (contentType === "library") {
        history.push(`/library/${targetId}`);
        return;
      }

      if (contentType === "libraryItem") {
        if (meta.libraryId) history.push(`/library/${meta.libraryId}/${targetId}`);
        return;
      }

      const courseId = contentType === "courseItem" ? meta.courseId : targetId;
      if (!courseId) {
        history.push(`/learn/${targetId}`);
        return;
      }

      const result = await sdk.account.license.startCourse(courseId);
      if (!result.success) {
        notify(result);
        return;
      }

      history.push(`/learn/${contentType === "courseItem" ? targetId : result.body.currentCourseItemId}`);
    },
  }), [sdk, page?.id, history, notify]);

  useEffect(() => {
    if (!containerRef.current) {
      setCourseListPortals([]);
      setCertListPortals([]);
      setPaymentMethodListPortals([]);
      setAccessListPortals([]);
      setGamificationPortals([]);
      setLeaderboardPortals([]);
      setPartnershipPortals([]);
      setForumPortals([]);
      setGroupChatPortals([]);
      return;
    }

    const clContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-course-list]");
    const clPortals: CourseListPortal[] = [];
    clContainers.forEach((el, i) => {
      el.innerHTML = "";
      clPortals.push({ container: el, props: { ...parseCourseListProps(el), sdk, folderParam: i === 0 ? "folder" : `folder${i + 1}` } });
    });
    setCourseListPortals(clPortals);

    const certContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-certificate-list]");
    const certPortals: CertificateListPortal[] = [];
    certContainers.forEach((el) => {
      el.innerHTML = "";
      certPortals.push({ container: el, props: { ...parseCertificateListProps(el), sdk } });
    });
    setCertListPortals(certPortals);

    const paymentMethodContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-payment-method-list]");
    const paymentMethodPortals: PaymentMethodListPortal[] = [];
    paymentMethodContainers.forEach((el) => {
      el.innerHTML = "";
      paymentMethodPortals.push({ container: el, props: { ...parsePaymentMethodListProps(el), sdk } });
    });
    setPaymentMethodListPortals(paymentMethodPortals);

    const accessContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-access-list]");
    const accessPortals: AccessListPortal[] = [];
    accessContainers.forEach((el) => {
      el.innerHTML = "";
      accessPortals.push({ container: el, props: { ...parseAccessListProps(el), sdk } });
    });
    setAccessListPortals(accessPortals);

    const gamContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-gamification-block]");
    const gamPortals: GamificationPortal[] = [];
    gamContainers.forEach((el) => {
      el.innerHTML = "";
      gamPortals.push({ container: el, props: { ...parseGamificationProps(el), sdk } });
    });
    setGamificationPortals(gamPortals);

    const lbContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-leaderboard]");
    const lbPortals: LeaderboardPortal[] = [];
    lbContainers.forEach((el) => {
      el.innerHTML = "";
      lbPortals.push({ container: el, props: { ...parseLeaderboardProps(el), sdk } });
    });
    setLeaderboardPortals(lbPortals);

    const partContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-partnership-block]");
    const partPortals: PartnershipPortal[] = [];
    partContainers.forEach((el) => {
      el.innerHTML = "";
      partPortals.push({ container: el, props: { ...parsePartnershipProps(el), sdk } });
    });
    setPartnershipPortals(partPortals);

    const forumContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-forum-block]");
    const fPortals: ForumPortal[] = [];
    forumContainers.forEach((el) => {
      el.innerHTML = "";
      fPortals.push({ container: el, props: { ...parseForumProps(el), sdk } });
    });
    setForumPortals(fPortals);

    const groupChatContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-group-chat-block]");
    const gcPortals: GroupChatPortal[] = [];
    groupChatContainers.forEach((el) => {
      el.innerHTML = "";
      gcPortals.push({ container: el, props: parseGroupChatProps(el) });
    });
    setGroupChatPortals(gcPortals);

    // CabinetMenu is rendered outside the page container by BasicLayout via
    // parseMenuFromHtml (reads the same [data-ez-cabinet-menu] placeholder from
    // the page's header/footer HTML), so the placeholder in the page body is
    // hidden to avoid a duplicate static preview.
    const menuContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-cabinet-menu]");
    menuContainers.forEach((el) => { el.style.display = "none"; });

    const sliderContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-slider]");
    sliderContainers.forEach((el) => initSlider(el, parseSliderProps(el)));

    const accordionContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-accordion]");
    accordionContainers.forEach((el) => initAccordion(el, parseAccordionProps(el)));

    const vendorAppContainers = containerRef.current.querySelectorAll<HTMLElement>("[data-ez-vendorapp]");
    vendorAppContainers.forEach((el) => initVendorAppBlock(el, parseVendorAppBlockProps(el), loc));
  }, [page?.id, page?.designHTMLPublished, sdk]);

  useEffect(() => {
    if (courseListPortals.length === 0) return;
    let cancelled = false;

    const load = async () => {
      const needCourses = courseListPortals.some(p => {
        const m = p.props.sourceMode;
        return m === "allCourses" || m === "all" || m === "specificCourses" || m === "specificMixed";
      });
      const needLibraries = courseListPortals.some(p => {
        const m = p.props.sourceMode;
        return m === "allLibraries" || m === "all" || m === "specificLibraries" || m === "specificMixed";
      });

      const promises: Promise<void>[] = [];

      if (needCourses) {
        promises.push((async () => {
          try {
            sdk.content.courses.store.updateFilter({
              courseType: "all",
              sectionType: SchoolContentSectionType.Course,
              onlyAvailable: true,
            });
            await sdk.content.courses.store.fetch();
            const items = sdk.content.courses.store.items || [];
            if (!cancelled) {
              setPrefetchedCourseItems(items);
              setPrefetchedCourses(extractCourses(items));
            }
          } catch (e) {
            console.error("[CabinetPageViewer] fetch courses error:", e);
            if (!cancelled) { setPrefetchedCourses([]); setPrefetchedCourseItems([]); }
          }
        })());
      } else {
        setPrefetchedCourses([]);
        setPrefetchedCourseItems([]);
      }

      if (needLibraries) {
        promises.push((async () => {
          try {
            sdk.content.libraries.store.updateFilter({
              sectionType: SchoolContentSectionType.Library,
              onlyAvailable: true,
              useBaseFilter: false,
            });
            await sdk.content.libraries.store.fetch();
            const libItems = sdk.content.libraries.store.items || [];
            if (!cancelled) {
              setPrefetchedLibraryItems(libItems);
              setPrefetchedLibraries(extractLibraries(libItems));
            }
          } catch (e) {
            console.error("[CabinetPageViewer] fetch libraries error:", e);
            if (!cancelled) { setPrefetchedLibraries([]); setPrefetchedLibraryItems([]); }
          }
        })());
      } else {
        setPrefetchedLibraries([]);
        setPrefetchedLibraryItems([]);
      }

      await Promise.all(promises);
    };

    load();
    return () => { cancelled = true; };
  }, [courseListPortals, sdk]);

  useEffect(() => {
    if (certListPortals.length === 0) {
      setPrefetchedCerts(undefined);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const fields = "{ id, number, createdDate, cert { id, name }, course { id, name }, image { id, cloudKey } }";
        const result = await sdk.api.get<{ items: any[] }>("/student/cert", fields);
        if (!cancelled && result.success && result.body?.items) {
          setPrefetchedCerts(result.body.items);
        }
      } catch (e) {
        console.error("[CabinetPageViewer] fetch certs error:", e);
        if (!cancelled) setPrefetchedCerts([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [certListPortals, sdk]);

  useEffect(() => {
    if (accessListPortals.length === 0) {
      setPrefetchedLicenses(undefined);
      setPrefetchedSubscriptions(undefined);
      setPrefetchedOrders(undefined);
      return;
    }
    let cancelled = false;

    const needLicenses = accessListPortals.some(p => p.props.contentType === "licenses");
    const needSubscriptions = accessListPortals.some(p => p.props.contentType === "subscriptions");
    const needOrders = accessListPortals.some(p => p.props.contentType === "orders");

    const promises: Promise<void>[] = [];

    if (needLicenses) {
      promises.push((async () => {
        try {
          const result = await sdk.account.license.fetch();
          if (!cancelled && result.success && result.body?.items) {
            setPrefetchedLicenses(result.body.items);
          }
        } catch (e) {
          console.error("[CabinetPageViewer] fetch licenses error:", e);
          if (!cancelled) setPrefetchedLicenses([]);
        }
      })());
    } else {
      setPrefetchedLicenses(undefined);
    }

    if (needSubscriptions) {
      promises.push((async () => {
        try {
          const result = await sdk.account.subscription.fetch();
          if (!cancelled && result.success && result.body?.items) {
            setPrefetchedSubscriptions(result.body.items);
          }
        } catch (e) {
          console.error("[CabinetPageViewer] fetch subscriptions error:", e);
          if (!cancelled) setPrefetchedSubscriptions([]);
        }
      })());
    } else {
      setPrefetchedSubscriptions(undefined);
    }

    if (needOrders) {
      promises.push((async () => {
        try {
          const fields = "{ id, number, createdDate, totalAmount, paidAmount, currency, status, fullyPaid, completed, contents { id, quantity, product { id, name, image { id, cloudKey } } } }";
          const result = await sdk.api.get<{ items: PurchaseOrder[] }>("/student/order", fields);
          if (!cancelled && result.success && result.body?.items) {
            setPrefetchedOrders(result.body.items);
          }
        } catch (e) {
          console.error("[CabinetPageViewer] fetch orders error:", e);
          if (!cancelled) setPrefetchedOrders([]);
        }
      })());
    } else {
      setPrefetchedOrders(undefined);
    }

    void Promise.all(promises);
    return () => { cancelled = true; };
  }, [accessListPortals, sdk]);

  useEffect(() => {
    if (gamificationPortals.length === 0) {
      setPrefetchedGamTotals(undefined);
      setPrefetchedGamAchievements(undefined);
      return;
    }
    let cancelled = false;

    const needTotals = gamificationPortals.some(p => p.props.blockType === "points");
    const needAchievements = gamificationPortals.some(p => p.props.blockType === "achievements");

    const promises: Promise<void>[] = [];

    if (needTotals) {
      promises.push((async () => {
        try {
          const res = await sdk.api.gamification.fetchTotals();
          if (!cancelled && res.success && res.body?.items) setPrefetchedGamTotals(res.body.items);
        } catch (e) { console.error("[CabinetPageViewer] fetch gamification totals error:", e); if (!cancelled) setPrefetchedGamTotals([]); }
      })());
    } else { setPrefetchedGamTotals(undefined); }

    if (needAchievements) {
      promises.push((async () => {
        try {
          const res = await sdk.api.gamification.fetchAchievements(new GamificationAchievementFilter({ take: 50 }));
          if (!cancelled && res.success && res.body?.items) setPrefetchedGamAchievements(res.body.items);
        } catch (e) { console.error("[CabinetPageViewer] fetch gamification achievements error:", e); if (!cancelled) setPrefetchedGamAchievements([]); }
      })());
    } else { setPrefetchedGamAchievements(undefined); }

    void Promise.all(promises);
    return () => { cancelled = true; };
  }, [gamificationPortals, sdk]);

  useEffect(() => {
    if (leaderboardPortals.length === 0) { setPrefetchedLeaderboardLeaders(undefined); return; }
    let cancelled = false;
    (async () => {
      try {
        const maxCols = Math.max(...leaderboardPortals.map(p => p.props.leaderColumns || 3));
        const res = await sdk.api.gamification.fetchLeaders(new GamificationLeaderFilter({ take: maxCols }));
        if (!cancelled && res.success && res.body?.items) setPrefetchedLeaderboardLeaders(res.body.items);
      } catch (e) {
        console.error("[CabinetPageViewer] fetch leaderboard leaders error:", e);
        if (!cancelled) setPrefetchedLeaderboardLeaders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [leaderboardPortals, sdk]);

  useEffect(() => {
    if (forumPortals.length === 0) { setPrefetchedForumData(undefined); return; }
    let cancelled = false;
    if (!sdk.forum?.thread) { setPrefetchedForumData(null); return; }
    sdk.forum.thread.fetch().then((res) => {
      if (!cancelled && res.success && res.body) setPrefetchedForumData(res.body);
    }).catch((e: unknown) => { console.error("[CabinetPageViewer] fetch forum error:", e); if (!cancelled) setPrefetchedForumData(null); });
    return () => { cancelled = true; };
  }, [forumPortals, sdk]);

  useEffect(() => {
    if (partnershipPortals.length === 0) {
      setPrefetchedPartTotals(undefined); setPrefetchedPartOffers(undefined);
      setPrefetchedPartOrders(undefined); setPrefetchedPartTransactions(undefined);
      setPrefetchedPartTransTotals(undefined);
      return;
    }
    let cancelled = false;
    const promises: Promise<void>[] = [];
    const needTotals = partnershipPortals.some(p => p.props.blockType === "totals");
    const needOffers = partnershipPortals.some(p => p.props.blockType === "offers");
    const needOrders = partnershipPortals.some(p => p.props.blockType === "orders");
    const needTransactions = partnershipPortals.some(p => p.props.blockType === "transactions");

    if (needTotals) {
      promises.push((async () => {
        try {
          const r = await sdk.api.partnership.fetchTotals({} as any);
          if (!cancelled && r.success && r.body?.items) setPrefetchedPartTotals(r.body.items);
        } catch (e) { console.error("[CabinetPageViewer] fetch partnership totals error:", e); if (!cancelled) setPrefetchedPartTotals([]); }
      })());
    } else { setPrefetchedPartTotals(undefined); }

    if (needOffers) {
      promises.push((async () => {
        try {
          await sdk.partnership.offerStore.fetch();
          if (!cancelled) setPrefetchedPartOffers(sdk.partnership.offerStore.items || []);
        } catch (e) { console.error("[CabinetPageViewer] fetch partnership offers error:", e); if (!cancelled) setPrefetchedPartOffers([]); }
      })());
    } else { setPrefetchedPartOffers(undefined); }

    if (needOrders) {
      promises.push((async () => {
        try {
          await sdk.partnership.orderStore.fetch();
          if (!cancelled) setPrefetchedPartOrders(sdk.partnership.orderStore.items || []);
        } catch (e) { console.error("[CabinetPageViewer] fetch partnership orders error:", e); if (!cancelled) setPrefetchedPartOrders([]); }
      })());
    } else { setPrefetchedPartOrders(undefined); }

    if (needTransactions) {
      promises.push((async () => {
        try {
          await sdk.partnership.transactionStore.fetch();
          if (!cancelled) setPrefetchedPartTransactions(sdk.partnership.transactionStore.items || []);
          const r = await (sdk.partnership.transactionStore as any).fetchTotals?.({});
          if (!cancelled && r?.success && r.body?.items) setPrefetchedPartTransTotals(r.body.items);
        } catch (e) {
          console.error("[CabinetPageViewer] fetch partnership transactions error:", e);
          if (!cancelled) { setPrefetchedPartTransactions([]); setPrefetchedPartTransTotals([]); }
        }
      })());
    } else { setPrefetchedPartTransactions(undefined); setPrefetchedPartTransTotals(undefined); }

    void Promise.all(promises);
    return () => { cancelled = true; };
  }, [partnershipPortals, sdk]);

  if (isLoading) return <LoadingOverlay />;

  if (!page) {
    return (
      <div className="flex align-center justify-center p-40" style={{ minHeight: 300 }}>
        <p style={{ color: "#aaa" }}>{loc.word("Cabinet.pageNotFound", { default: "Page not found" })}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="cabinet-page" style={pageContainerStyle}>
      {/* themeCss is generated on the server by CabinetThemeHelper from admin-only
          input. It is already scoped to .cabinet-page, @-rules and </style> breakouts
          are rejected there, so it's safe to inject as-is. Do NOT accept theme CSS
          from any untrusted source — add client-side validation first. */}
      {themeCss && <style>{themeCss}</style>}
      {page.header && (
        <ComponentView component={page.header} type="header" userIsInFilter={userIsInFilter} currentUserId={currentUserId} clickActions={clickActions} formProps={formProps} />
      )}
      <SchoolEZEditorView
        key={page.id}
        html={page.designHTMLPublished ?? ""}
        style={page.designStylePublished ?? ""}
        currentUserId={currentUserId}
        userIsInFilter={userIsInFilter}
        wrapperStyle={contentStyle}
        clickActions={clickActions}
        formProps={formProps}
      />
      {page.footer && (
        <ComponentView component={page.footer} type="footer" userIsInFilter={userIsInFilter} currentUserId={currentUserId} clickActions={clickActions} formProps={formProps} />
      )}
      {courseListPortals.map((portal, i) => {
        const isFolders = portal.props.displayMode === "folders";
        const contentItems = isFolders
          ? [...(prefetchedCourseItems || []), ...(prefetchedLibraryItems || [])]
          : undefined;
        return createPortal(
          <CourseListWidget
            key={i}
            {...portal.props}
            prefetchedCourses={prefetchedCourses}
            prefetchedLibraries={prefetchedLibraries}
            prefetchedContentItems={contentItems}
          />,
          portal.container,
        );
      })}
      {certListPortals.map((portal, i) =>
        createPortal(
          <CertificateListWidget
            key={i}
            {...portal.props}
            prefetchedCerts={prefetchedCerts}
          />,
          portal.container,
        ),
      )}
      {paymentMethodListPortals.map((portal, i) =>
        createPortal(
          <PaymentMethodListWidget
            key={i}
            {...portal.props}
          />,
          portal.container,
        ),
      )}
      {accessListPortals.map((portal, i) =>
        createPortal(
          <AccessListWidget
            key={i}
            {...portal.props}
            prefetchedLicenses={prefetchedLicenses}
            prefetchedSubscriptions={prefetchedSubscriptions}
            prefetchedOrders={prefetchedOrders}
          />,
          portal.container,
        ),
      )}
      {gamificationPortals.map((portal, i) =>
        createPortal(
          <GamificationWidget
            key={i}
            {...portal.props}
            prefetchedTotals={prefetchedGamTotals}
            prefetchedAchievements={prefetchedGamAchievements}
          />,
          portal.container,
        ),
      )}
      {leaderboardPortals.map((portal, i) =>
        createPortal(
          <LeaderboardWidget
            key={i}
            {...portal.props}
            prefetchedLeaders={prefetchedLeaderboardLeaders}
          />,
          portal.container,
        ),
      )}
      {forumPortals.map((portal, i) =>
        createPortal(
          <ForumWidget key={i} {...portal.props} prefetchedForumData={prefetchedForumData} />,
          portal.container,
        ),
      )}
      {groupChatPortals.map((portal, i) =>
        createPortal(<GroupChatWidget key={i} {...portal.props} />, portal.container),
      )}
      {partnershipPortals.map((portal, i) =>
        createPortal(
          <PartnershipWidget
            key={i}
            {...portal.props}
            prefetchedTotals={prefetchedPartTotals}
            prefetchedOffers={prefetchedPartOffers}
            prefetchedOrders={prefetchedPartOrders}
            prefetchedTransactions={prefetchedPartTransactions}
            prefetchedTransactionTotals={prefetchedPartTransTotals}
          />,
          portal.container,
        ),
      )}
    </div>
  );
};
